/**
 * GEN-3039 (SEC-01) — mensagens dos erros de limite de tentativas nas telas de autenticação.
 *
 * Existe porque, sem isso, o backend passa a responder 429 e o `switch` do `LoginForm` cai no
 * `default`, que diz "Email ou Senha são inválidos." — o usuário bloqueado não descobre que está
 * bloqueado, tenta de novo, e cada tentativa reacende o contador. Ou seja: a proteção nova pioraria
 * a experiência exatamente de quem ela deveria proteger.
 *
 * Os códigos vêm do backend em SCREAMING_SNAKE_CASE (convenção de erro do repo):
 *  - `AUTH_TOO_MANY_ATTEMPTS`   → rate limit da ROTA (guard de throttle)
 *  - `LOGIN_TEMPORARILY_LOCKED` → bloqueio temporal da CONTA por senha errada
 *  - `RESET_NOT_FOUND`          → código de recuperação inválido, expirado, consumido ou queimado
 *                                 (o servidor NÃO distingue, de propósito: separar viraria oráculo
 *                                 de enumeração de e-mails)
 *  - `VALIDATION_CODE_EXPIRED`  → código de ativação expirado (um novo já foi enviado por e-mail)
 */

export const AUTH_THROTTLE_CODES = ["AUTH_TOO_MANY_ATTEMPTS", "LOGIN_TEMPORARILY_LOCKED"] as const;

export const isAuthThrottleCode = (code?: string): boolean =>
  !!code && (AUTH_THROTTLE_CODES as readonly string[]).includes(code);

/**
 * Formata a espera em linguagem de gente. Arredonda para CIMA de propósito: dizer "1 minuto" e o
 * usuário voltar em 50s para tomar outro 429 é pior que dizer 2 e ele voltar liberado.
 */
export const formatRetryAfter = (seconds?: number): string => {
  if (!seconds || seconds <= 0) return "alguns instantes";
  if (seconds < 60) return `${Math.ceil(seconds)} segundos`;

  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return minutes === 1 ? "1 minuto" : `${minutes} minutos`;

  const hours = Math.ceil(minutes / 60);
  return hours === 1 ? "1 hora" : `${hours} horas`;
};

/**
 * Contexto da tela que mostra a mensagem — define a SAÍDA sugerida ao usuário.
 *
 * Existe porque a saída correta depende de onde o usuário está: na tela de login, quem esqueceu a
 * senha deve recuperá-la em vez de esperar; nas telas de código (ativação de conta, OTP de
 * recuperação, nova senha) a saída é pedir um código novo — sugerir "recupere sua senha" ali é
 * irrelevante na ativação (não existe senha no fluxo ainda) e circular na recuperação (é
 * exatamente o que o usuário já está fazendo).
 */
/**
 * GEN-3739: `access` é a variante NEUTRA, para a tela de validação — ela atende dois públicos ao
 * mesmo tempo (quem recebeu um código de 6 dígitos e quem recebeu o link do convite), e o servidor
 * não conta ao cliente qual dos dois é. Sugerir "solicite um novo código" a quem só tem um link
 * como saída é a mesma falsa pista que o card veio corrigir.
 */
export type AuthThrottleContext = "login" | "code" | "access";

/**
 * Mensagem do erro de limite de tentativas.
 *
 * Diz o que aconteceu, quanto esperar e qual é a saída — o padrão de mensagem de erro recuperável
 * (Nielsen H9). Sem a saída, o usuário que de fato esqueceu a senha (ou cujo código expirou) fica
 * esperando 15 minutos só para errar de novo.
 */
export const authThrottleMessage = (retryAfter?: number, context: AuthThrottleContext = "login"): string => {
  const exit =
    context === "login"
      ? "ou recupere sua senha para entrar agora."
      : context === "access"
      ? "ou solicite um novo envio."
      : "ou solicite um novo código.";

  return `Muitas tentativas. Aguarde ${formatRetryAfter(retryAfter)} antes de tentar novamente, ${exit}`;
};
